/* global gtag */
import React, { useState, useEffect } from "react";
import { styled } from "@mui/system";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  CssBaseline,
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  Stack,
  useMediaQuery,
  useTheme,
  Badge,
  Chip,
} from "@mui/material";
import {
  PageTitle,
  CenteredMessage,
} from "../../../components/JobManagementComponents";
import CreateJobForm from "./CreateJobForm";
import EditJobForm from "./EditJobForm";
import dayjs from "dayjs";
import logoImage from "../../../assets/new_logo.png";
import { fetchJobAPI, deleteJobAPI } from "../../../services/jobService";
import Application from "../Application/Application";
import { useDispatch } from "react-redux";
import { selectJob } from "../../../store/reducers/appreducer";
import { paths } from "../../../routes/paths";
import JobCard from "./JobCard"; 

const Root = styled("div")({
  display: "flex",
});

const PaginationContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: "20px 0",
});

const defaultPagination = {
  page: 1,
  pageSize: 6,
};

const JobManagement = ({ onNavigate }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [showForm, setShowForm] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [editingJob, setEditingJob] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [pagination, setPagination] = useState(defaultPagination);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("token");
    dispatch(selectJob(null));
    if (!token) {
      navigate("/signin", { replace: true });
    }
  }, [location, navigate, dispatch]);

  // Check for create query parameter and show form
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('create') === 'true') {
      setShowForm(true);
      // Clean up URL after opening form
      navigate(paths.dashboard.jobs, { replace: true });
    }
  }, [location.search, navigate]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await fetchJobAPI(pagination.page, pagination.pageSize);
      setTotalPages(response.total_pages || 1);
      setTotalJobs(response.total_jobs || 0);
      setJobs(Array.isArray(response.jobs) ? response.jobs : []);
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setJobs([]);
      setTotalJobs(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [pagination]);

  const handlePageChange = (event, value) => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    setPagination({
      ...pagination,
      page: value,
    });
  };

  const handleCancel = () => {
    setShowForm(false);
  };

  const handleSave = async () => {
    setShowForm(false);
    await fetchJobs();
  };

  const handleEditJob = (index) => {
    setEditingJob(jobs[index]);
  };

  const handleSaveJob = async () => {
    setEditingJob(null);
    await fetchJobs();
  };

  const handleCancelEdit = () => {
    setEditingJob(null);
  };

  const handleOpenConfirmDialog = (index) => {
    setJobToDelete(index);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setJobToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (jobToDelete !== null && jobs[jobToDelete]) {
      const jobId = jobs[jobToDelete]["Job Id"];
      try {
        await deleteJobAPI(jobId);
        await fetchJobs();
      } catch (error) {
        console.error("Error deleting job:", error);
      }
    }
    handleCloseConfirmDialog();
  };

  const handleAddResume = (job) => {
    dispatch(selectJob(job));
    const jobId = job["Job Id"];
    navigate(`${paths.dashboard.applications}/${jobId}`);
  };

  if (selectedJob) {
    return <Application job={selectedJob} />;
  }

  // grid sizing based on screen size
  const getGridSize = () => {
    if (isMobile) return 12; 
    if (isTablet) return 6;  
    return 4;                
  };

  // Get active jobs count
  const getActiveJobsCount = () => {
    return jobs.filter(job => job.status === "Active" || job.Status === "Active" || 
                             (!job.status && !job.Status)).length; // Default to active if no status
  };

  return (
    <Root>
      <CssBaseline />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          // backgroundColor: "#F3F7FF",
          minHeight: "100vh",
          width: "100%",
        }}
      >
        {showForm ? (
          <CreateJobForm onSave={handleSave} onCancel={handleCancel} />
        ) : editingJob ? (
          <EditJobForm
            job={editingJob}
            onSave={handleSaveJob}
            onCancel={handleCancelEdit}
          />
        ) : loading ? (
          <CenteredMessage>
            <CircularProgress />
          </CenteredMessage>
        ) : jobs.length === 0 ? (
          <CenteredMessage>
            Click on CREATE JOB in the top bar to create a new job
          </CenteredMessage>
        ) : (
          <>
            {/* Job Cards Header */}
            <Box 
              sx={{ 
                mb: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 2 : 0
              }}
            >
              <Typography 
                variant="h6" 
                sx={{
                  fontSize: { xs: '18px', sm: '20px' },
                  fontWeight: 600,
                  color: '#1f2937',
                  fontFamily: 'Raleway'
                }}
              >
                Job Positions ({totalJobs})
              </Typography>
{/*               
              <Chip
                label={`${getActiveJobsCount()} Active`}
                sx={{
                  backgroundColor: '#dbeafe',
                  color: '#1d4ed8',
                  fontWeight: 500,
                  fontSize: '14px',
                  '& .MuiChip-label': {
                    px: 2,
                    py: 0.5
                  }
                }}
                variant="filled"
              /> */}
            </Box>

            {/* Job Cards Grid */}
            <Grid container spacing={3}>
              {jobs.map((job, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <JobCard
                    job={job}
                    index={index}
                    logoImage={logoImage}
                    onEdit={handleEditJob}
                    onDelete={handleOpenConfirmDialog}
                    onViewApplication={handleAddResume}
                  />
                </Grid>
              ))}
            </Grid>
            
            {totalPages > 1 && (
              <PaginationContainer>
                <Stack spacing={2}>
                  <Pagination
                    count={totalPages}
                    page={pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? "medium" : "large"}
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </PaginationContainer>
            )}
          </>
        )}

        {/* Confirmation Dialog */}
        <Dialog 
          open={openConfirmDialog} 
          onClose={handleCloseConfirmDialog}
          fullWidth={isMobile}
          maxWidth="sm"
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this job?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDialog} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="secondary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Root>
  );
};

export default JobManagement;